import { fn } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { type Task, task, timeout } from 'ember-concurrency';
import { RATES_DATA_START } from 'frontend-utils/utils/constants/constant-values';
import GeneralCalendarsCalendarRange from 'lumen/components/general/calendars/calendar-range';
import GeneralDropdownsDropdownYield, {
  type Dropdown,
} from 'lumen/components/general/dropdowns/dropdown-yield';
import GeneralDropdownsSidebar from 'lumen/components/general/dropdowns/sidebar';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';

import styles from './comparison-picker.module.css';
import HotelNetRevenueDatepickersComparisonPickerTriggerTarget from './comparison-picker/trigger-target';

interface Range {
  end: Dayjs;
  start: Dayjs;
}

interface SidebarOption {
  count?: number;
  displayRange: boolean;
  dropdownText?: string;
  hasCalendar: boolean;
  id: string;
  isDisabled: boolean;
  isSelected: boolean;
  range: Range;
  text: string;
}

interface SidebarList {
  id: string;
  options: SidebarOption[];
  title?: string;
}

interface HotelNetRevenueDatepickersComparisonPickerSignature {
  Args: {
    maxDate?: Dayjs;
    maxRange?: string;
    minDate?: Dayjs | null;
    numberOfDays: number;
    range: { end: Dayjs | null; start: Dayjs };
    rangeChanged: (range: Range, optionId: string) => void;
    selectedOptionId: string;
    sidebarLists: SidebarList[];
  };
}

export default class HotelNetRevenueDatepickersComparisonPicker extends Component<HotelNetRevenueDatepickersComparisonPickerSignature> {
  // incrementing this tracked value signals the calendar to re-focus
  @tracked calendarRefocusTrigger = 0;
  @tracked pendingCustomRange: Range | null = null;
  @tracked pendingOptionId: string | null = null;

  selectOptionDebouncedTask: Task<void, [SidebarOption]> = task(
    async (option: SidebarOption) => {
      // yield one tick so the click ripple settles before state update
      await timeout(0);
      this.pendingOptionId = option.id;
      if (option.id !== 'custom') {
        this.pendingCustomRange = null;
      }
      this.calendarRefocusTrigger++;
    },
  );

  triggerComponent = HotelNetRevenueDatepickersComparisonPickerTriggerTarget;

  get activeOption(): SidebarOption | undefined {
    return this.allOptions.find((o) => o.id === this.activeOptionId);
  }

  get activeOptionId(): string {
    return this.pendingOptionId ?? this.args.selectedOptionId;
  }

  get allOptions(): SidebarOption[] {
    return this.args.sidebarLists.flatMap((list) => list.options);
  }

  get calendarRange(): Range {
    const { range } = this.args;
    const fallback: Range = {
      start: range.start,
      end: range.end ?? range.start,
    };

    if (this.activeOptionId === 'custom') {
      return this.pendingCustomRange ?? fallback;
    }

    return this.activeOption?.range ?? fallback;
  }

  get effectiveMaxDate(): Dayjs {
    return this.args.maxDate ?? dayjs().add(2, 'year').endOf('month');
  }

  get effectiveMinDate(): Dayjs {
    return this.args.minDate ?? dayjs(RATES_DATA_START);
  }

  get isApplyDisabled(): boolean {
    if (this.pendingOptionId === null) {
      return true;
    }

    if (this.activeOptionId !== 'custom') {
      return this.activeOptionId === this.args.selectedOptionId;
    }

    if (!this.pendingCustomRange) {
      return true;
    }

    const { start, end } = this.args.range;
    return (
      this.pendingCustomRange.start.isSame(start, 'day') &&
      this.pendingCustomRange.end.isSame(end ?? start, 'day')
    );
  }

  get sidebarListsWithSelection(): SidebarList[] {
    return this.args.sidebarLists.map((list) => ({
      ...list,
      options: list.options.map((option) => ({
        ...option,
        isSelected: option.id === this.activeOptionId,
      })),
    }));
  }

  get triggerLabel(): string | Range {
    if (this.activeOptionId === 'custom') {
      return this.calendarRange;
    }

    return this.activeOption?.dropdownText ?? this.activeOptionId;
  }

  resetPendingState(): void {
    this.pendingOptionId = null;
    this.pendingCustomRange = null;
  }

  @action applyChanges(dropdown: Dropdown): void {
    const range =
      this.activeOptionId === 'custom'
        ? (this.pendingCustomRange ?? this.calendarRange)
        : (this.activeOption?.range ?? this.calendarRange);

    this.args.rangeChanged(range, this.activeOptionId);
    this.resetPendingState();
    dropdown.toggleDropdown();
  }

  @action cancelChanges(dropdown: Dropdown): void {
    this.resetPendingState();
    dropdown.toggleDropdown();
  }

  @action onCancel(): void {
    this.resetPendingState();
  }

  @action selectOption(option: SidebarOption): void {
    void this.selectOptionDebouncedTask.perform(option);
  }

  @action selectRange(selectedRange: { moment: { start?: Dayjs } }): void {
    const { start } = selectedRange.moment;
    if (!start) {
      return;
    }
    this.pendingCustomRange = {
      start,
      end: start.add(this.args.numberOfDays, 'day'),
    };
    this.pendingOptionId = 'custom';
  }

  <template>
    <div class="flex flex-row items-center justify-start">
      <GeneralDropdownsDropdownYield
        @dropdownClass=".dropdown-target"
        {{! @glint-expect-error: Incorrect type }}
        @dropdownClassName="dropdown-target"
        @onCancel={{this.onCancel}}
        @selected={{this.triggerLabel}}
        @triggerComponent={{this.triggerComponent}}
        as |dropdown|
      >
        <section class="date-picker-with-side-bar-calendar">
          <section class={{styles.calendar-content}}>
            <div class="mb-500">
              <GeneralCalendarsCalendarRange
                @calendarFocusOnEnd={{null}}
                @canSelectMonth={{false}}
                @dateTooltips={{null}}
                @displayEvents={{false}}
                @maxDate={{this.effectiveMaxDate}}
                @maxRange={{@maxRange}}
                @minDate={{this.effectiveMinDate}}
                @refocusMonths={{this.calendarRefocusTrigger}}
                @selectRange={{this.selectRange}}
                @selectedRange={{this.calendarRange}}
              />
            </div>
            <GeneralDropdownsSidebar
              @selectOption={{this.selectOption}}
              @sidebarLists={{this.sidebarListsWithSelection}}
            />
          </section>
          <section class={{styles.calendar-footer}}>
            <div class={{styles.save-cancel}}>
              <GeneralFormFieldsCancelAndConfirm
                @cancel={{fn this.cancelChanges dropdown}}
                @confirm={{fn this.applyChanges dropdown}}
                @isConfirmDisabled={{this.isApplyDisabled}}
              />
            </div>
          </section>
        </section>
      </GeneralDropdownsDropdownYield>
    </div>
  </template>
}
